/**
 * Sub-controller for the "Load Build" view of the configurator navigation modal.
 *
 * Wraps the 6-chunk unique-code form with OTP-style UX (auto-advance focus, paste distribution,
 * Backspace/Delete shifting, arrow nav) and submits the aggregated code via AJAX to the
 * `SaveBuild-LoadConfigurationURL` endpoint declared on the form's `data-action`.
 */
export const loadBuild = {
  /**
   * Bootstrap the load-build view. Idempotent; safe to call once at boot.
   */
  loadBuildView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * Number of code chunks; matches the template's `<input class="code-chunk">` count and the
     * server-side validation in `SaveBuild-LoadConfigurationURL`.
     *
     * @readonly
     */
    codeLength: 6,

    /**
     * @type {boolean}
     */
    isWired: false,

    /**
     * @type {{
     *   view: HTMLElement | null,
     *   modalContent: HTMLElement | null,
     *   form: HTMLFormElement | null,
     *   chunksContainer: HTMLElement | null,
     *   chunks: HTMLInputElement[],
     *   feedback: HTMLElement | null
     * }}
     */
    elements: {
      view: null,
      modalContent: null,
      form: null,
      chunksContainer: null,
      chunks: [],
      feedback: null
    },

    /**
     * Wire on first appearance of the view. Mirrors `changeModel.helpers.bootstrap` so the heavy
     * DOM querying happens lazily — not on every PDP boot.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view === 'load-build') {
          this.wire();
        }
      });

      if (sessionStorage.getItem('buildLoadedToastPending') === '1') {
        sessionStorage.removeItem('buildLoadedToastPending');
        $('.toast-build-loaded').toast('show');
      }
    },

    /**
     * Resolve element refs and attach DOM listeners. No-op after the first successful run.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="load-build"]');

      if (!view) {
        return false;
      }

      const form = view.querySelector('.load-build-form');
      const chunksContainer = form?.querySelector('.code-chunks');
      const chunks = chunksContainer ? Array.from(chunksContainer.querySelectorAll('.code-chunk')) : [];
      const feedback = chunksContainer?.querySelector('.invalid-feedback');

      if (!form || !chunksContainer || chunks.length !== this.codeLength) {
        return false;
      }

      this.elements.view = view;
      this.elements.modalContent = view.closest('.modal-content');
      this.elements.form = form;
      this.elements.chunksContainer = chunksContainer;
      this.elements.chunks = chunks;
      this.elements.feedback = feedback;

      chunks.forEach((chunk, index) => {
        chunk.addEventListener('input', event => this.onChunkInput(event, index));
        chunk.addEventListener('keydown', event => this.onChunkKeydown(event, index));
        chunk.addEventListener('paste', event => this.onChunkPaste(event, index));
        chunk.addEventListener('focus', () => chunk.select());
      });

      form.addEventListener('submit', event => this.onSubmit(event));

      this.isWired = true;

      return true;
    },

    /**
     * `input` delegate — auto-advances focus across the trailing chunks. Multi-char inputs
     * (e.g. fast typers, mobile autocorrect) are spread one-per-chunk so the read-back via
     * {@link loadBuild.helpers.readCode} stays predictable regardless of how chars arrived.
     *
     * @param {InputEvent} event
     * @param {number} index
     */
    onChunkInput(event, index) {
      const chunk = event.target;
      const chars = [...chunk.value];

      this.clearError();

      if (chars.length === 0) {
        return;
      }

      this.distributeChars(index, chars);
      this.focusChunk(Math.min(this.codeLength - 1, index + chars.length));
    },

    /**
     * `keydown` delegate — Backspace/Delete shift trailing chunks left, Arrow keys navigate.
     * Other keys fall through to the native input behaviour (constrained by `maxlength="1"`).
     *
     * @param {KeyboardEvent} event
     * @param {number} index
     */
    onChunkKeydown(event, index) {
      const chunk = event.target;
      const lastIndex = this.codeLength - 1;

      switch (event.key) {
        case 'Backspace':
          if (chunk.value === '' && index > 0) {
            this.shiftLeftFrom(index);
            this.focusChunk(index - 1);
          }
          break;

        case 'Delete':
          if (index < lastIndex) {
            event.preventDefault();
            this.shiftLeftFrom(index + 1);
            chunk.select();
          }
          break;

        case 'ArrowLeft':
          if (index > 0) {
            event.preventDefault();
            this.focusChunk(index - 1);
          }
          break;

        case 'ArrowRight':
          if (index < lastIndex) {
            event.preventDefault();
            this.focusChunk(index + 1);
          }
          break;
      }
    },

    /**
     * `paste` delegate — runs BEFORE `input`, so we cancel the native paste and distribute the
     * clipboard ourselves. Without this, `maxlength="1"` would silently drop everything past the
     * first character of a 6-digit paste.
     *
     * @param {ClipboardEvent} event
     * @param {number} index
     */
    onChunkPaste(event, index) {
      event.preventDefault();

      const chars = [...(event.clipboardData?.getData('text') ?? '')];

      if (chars.length === 0) {
        return;
      }

      this.distributeChars(index, chars);
      this.focusChunk(Math.min(this.codeLength - 1, index + chars.length));
      this.clearError();
    },

    /**
     * Submit handler — short-circuits incomplete codes and POSTs the aggregated value to the
     * server endpoint declared on `form.data-action`. Any failure (validation, network, server)
     * surfaces as the same inline error state; the spinner overlay reuses the SFRA jQuery plugin.
     *
     * @param {SubmitEvent} event
     */
    onSubmit(event) {
      event.preventDefault();

      const code = this.readCode();

      if (code.length !== this.codeLength) {
        this.showError();
        return;
      }

      const url = this.elements.form.dataset.action;

      if (!url) {
        return;
      }

      const $modalContent = $(this.elements.modalContent);

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: { uniqueBuildId: code },
        beforeSend: () => $modalContent.spinner().start(),
        success: (response) => {
          if (response?.result?.success && response?.redirectURL) {
            sessionStorage.setItem('buildLoadedToastPending', '1');
            window.location.assign(response.redirectURL);
          } else {
            this.showError(response?.message);
          }
        },
        error: (jqXHR) => this.showError(jqXHR.responseJSON?.message),
        complete: () => $modalContent.spinner().stop()
      });
    },

    /**
     * Write `chars` into the chunks starting at `startIndex`, clipping at the last chunk.
     *
     * @param {number} startIndex
     * @param {string[]} chars
     */
    distributeChars(startIndex, chars) {
      chars.forEach((char, offset) => {
        const target = this.elements.chunks[startIndex + offset];

        if (target) {
          target.value = char;
        }
      });
    },

    /**
     * Pull every chunk value one step to the left starting at `index`, clearing the last slot.
     * Mirrors the original `uniqueCodeInputFill`'s Backspace/Delete behaviour.
     *
     * @param {number} index
     */
    shiftLeftFrom(index) {
      const chunks = this.elements.chunks;

      for (let i = index; i < chunks.length - 1; i++) {
        chunks[i].value = chunks[i + 1].value;
      }

      chunks[chunks.length - 1].value = '';
    },

    /**
     * Focus + select the chunk at `index`. Selecting (vs. just focusing) lets the next keystroke
     * overwrite the existing char without an explicit Backspace.
     *
     * @param {number} index
     */
    focusChunk(index) {
      const chunk = this.elements.chunks[index];

      chunk?.focus();
      chunk?.select();
    },

    /**
     * Concatenate every chunk into the unique-code string. Empty chunks contribute an empty
     * string, so `value.length` doubles as the validity check.
     *
     * @returns {string}
     */
    readCode() {
      return this.elements.chunks.map(chunk => chunk.value).join('');
    },

    /**
     * Flag the chunks group as invalid — Bootstrap's `.is-invalid ~ .invalid-feedback` rule
     * reveals the inline error text without any custom CSS on our side.
     *
     * @param {string} message
     */
    showError(message = '') {
      if (this.elements.feedback && message) {
        this.elements.feedback.textContent = message;
      }

      this.elements.chunksContainer.classList.add('is-invalid');
      this.elements.chunks.forEach(chunk => chunk.classList.add('is-invalid'));
    },

    /**
     * Clear the invalid state — called whenever the user types or pastes.
     */
    clearError() {
      let isInvalid = false;

      for (const chunk of this.elements.chunks) {
        isInvalid = chunk.classList.toggle('is-invalid', !chunk.checkValidity()) || isInvalid;
      }

      this.elements.chunksContainer.classList.toggle('is-invalid', isInvalid);
    }
  }
};
